import React, { useState } from "react";
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, MenuItem, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { COST_CATEGORIES, CostEntry, useCosts, useCreateCost, useDeleteCost } from "../../../api/analytics";
import { formatCents } from "../../../api/fiscalite";
import { usePlateResolver } from "../../../api/vehicleOptions";
import { VehiclePicker } from "../../../components/VehiclePicker/VehiclePicker";

interface Props {
  fleetId: number;
}

const EMPTY = {
  vehicle_id: null as number | null,
  category: "FUEL",
  occurred_on: new Date().toISOString().slice(0, 10),
  amount_ht: "",
  vat: "",
  vat_recoverable: "",
  quantity: "",
  unit: "L",
  odometer_km: "",
  label: "",
};

/**
 * Cost ledger.
 *
 * This is where the data that makes every other screen possible comes in.
 * Amounts are entered in euros and converted to cents once, server-side —
 * two conversion sites is one too many, and the second one always drifts.
 *
 * Recording the odometer with a cost is optional in the form but decisive in
 * practice: without it there is no distance, and without distance there is no
 * cost per kilometre.
 */
export const Couts: React.FC<Props> = ({ fleetId }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ ...EMPTY });
  const [filters, setFilters] = useState<{ category?: string; vehicle_id?: number }>({});

  const { costsPayload, isLoading } = useCosts(fleetId, filters);
  const create = useCreateCost(fleetId);
  const remove = useDeleteCost(fleetId);
  const plateOf = usePlateResolver(fleetId);

  const rows: CostEntry[] = costsPayload?.data ?? [];

  // An empty selection means "no filter", so the key is removed rather than
  // sent as null — the query key stays stable and the server keeps its
  // `filled()` semantics.
  const updateFilter = (key: "category" | "vehicle_id", value: string | number | null) => {
    setFilters((current) => {
      const next = { ...current };
      if (value === null || value === "") {
        delete next[key];
      } else if (key === "vehicle_id") {
        next.vehicle_id = Number(value);
      } else {
        next.category = String(value);
      }
      return next;
    });
  };

  const submit = async () => {
    if (form.vehicle_id === null) return;

    await create.mutateAsync({
      vehicle_id: form.vehicle_id,
      category: form.category,
      occurred_on: form.occurred_on,
      amount_ht: Number(form.amount_ht),
      vat: form.vat ? Number(form.vat) : undefined,
      vat_recoverable: form.vat_recoverable ? Number(form.vat_recoverable) : undefined,
      quantity: form.quantity ? Number(form.quantity) : undefined,
      unit: form.quantity ? form.unit : undefined,
      odometer_km: form.odometer_km ? Number(form.odometer_km) : undefined,
      label: form.label || undefined,
    });
    setForm({ ...EMPTY });
  };

  const canSubmit = form.vehicle_id !== null && form.amount_ht !== "" && !create.isPending;

  return (
    <Stack spacing={3}>
      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {t("costs.add", "Ajouter un cout")}
          </Typography>

          <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "repeat(4, 1fr)" } }}>
            <VehiclePicker
              fleetId={fleetId}
              value={form.vehicle_id}
              onChange={(vehicleId) => setForm({ ...form, vehicle_id: vehicleId })}
              label={t("costs.vehicle", "Vehicule")}
              required
            />
            <TextField
              select size="small" label={t("costs.category", "Categorie")} value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
            >
              {COST_CATEGORIES.map((category) => (
                <MenuItem key={category} value={category}>{category}</MenuItem>
              ))}
            </TextField>
            <TextField
              size="small" type="date" label={t("costs.date", "Date")} value={form.occurred_on}
              onChange={(e) => setForm({ ...form, occurred_on: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small" label={t("costs.amountHt", "Montant HT (EUR)")} value={form.amount_ht}
              onChange={(e) => setForm({ ...form, amount_ht: e.target.value })}
            />
            <TextField
              size="small" label={t("costs.vat", "TVA (EUR)")} value={form.vat}
              onChange={(e) => setForm({ ...form, vat: e.target.value })}
            />
            <TextField
              size="small" label={t("costs.vatRecoverable", "TVA recuperable (EUR)")} value={form.vat_recoverable}
              onChange={(e) => setForm({ ...form, vat_recoverable: e.target.value })}
              helperText={t("costs.vatHint", "Ne peut pas depasser la TVA payee")}
            />
            <TextField
              size="small" label={t("costs.quantity", "Quantite (L / kWh)")} value={form.quantity}
              onChange={(e) => setForm({ ...form, quantity: e.target.value })}
            />
            <TextField
              size="small" label={t("costs.odometer", "Kilometrage")} value={form.odometer_km}
              onChange={(e) => setForm({ ...form, odometer_km: e.target.value })}
              helperText={t("costs.odometerHint", "Necessaire au cout au km")}
            />
          </Box>

          <Stack direction="row" spacing={2} sx={{ mt: 2 }} alignItems="center">
            <TextField
              size="small" label={t("costs.label", "Libelle")} value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              sx={{ flex: 1 }}
            />
            <Button variant="contained" onClick={submit} disabled={!canSubmit}>
              {create.isPending ? t("common.loading", "Enregistrement...") : t("common.add", "Ajouter")}
            </Button>
          </Stack>

          {create.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {(create.error as any)?.response?.data?.message ?? t("common.error", "Une erreur est survenue.")}
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ mb: 1 }}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              {t("costs.ledger", "Journal des couts")}
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <VehiclePicker
                fleetId={fleetId}
                value={filters.vehicle_id ?? null}
                onChange={(vehicleId) => updateFilter("vehicle_id", vehicleId)}
                label={t("costs.vehicle", "Vehicule")}
                allowAll
                sx={{ minWidth: 260 }}
              />
              <TextField
                select size="small" label={t("costs.category", "Categorie")}
                value={filters.category ?? ""}
                onChange={(e) => updateFilter("category", e.target.value)}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="">{t("common.all", "Toutes")}</MenuItem>
                {COST_CATEGORIES.map((category) => (
                  <MenuItem key={category} value={category}>{category}</MenuItem>
                ))}
              </TextField>
            </Stack>
          </Stack>

          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("costs.date", "Date")}</TableCell>
                  <TableCell>{t("costs.vehicle", "Vehicule")}</TableCell>
                  <TableCell>{t("costs.category", "Categorie")}</TableCell>
                  <TableCell align="right">{t("costs.amountHt", "HT")}</TableCell>
                  <TableCell align="right">{t("costs.vat", "TVA")}</TableCell>
                  <TableCell>{t("costs.source", "Source")}</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{String(row.occurred_on).slice(0, 10)}</TableCell>
                    <TableCell>{plateOf(row.vehicle_id)}</TableCell>
                    <TableCell>{row.category}</TableCell>
                    <TableCell align="right">{formatCents(row.amount_ht_cents)}</TableCell>
                    <TableCell align="right">
                      {formatCents(row.vat_cents)}
                      {row.vat_cents > row.vat_recoverable_cents && (
                        <Chip
                          size="small" variant="outlined" sx={{ ml: 1 }}
                          label={`${t("costs.lost", "perdue")} ${formatCents(row.vat_cents - row.vat_recoverable_cents)}`}
                        />
                      )}
                    </TableCell>
                    <TableCell><Chip size="small" variant="outlined" label={row.source} /></TableCell>
                    <TableCell align="right">
                      <Button size="small" color="inherit" onClick={() => remove.mutate(row.id)}>
                        {t("common.delete", "Supprimer")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
};
